import {PaletteMode} from "@mui/material";

/**
 * Concrete return shapes instead of MUI's `SxProps<Theme>` union: that union type does not expose
 * `color`/`opacity`/pseudo-selector keys for direct property access at the call site (it also allows
 * function and array forms this module never returns), so callers merging these fragments into an `sx`
 * object would need a cast. Each shape below is a plain object, structurally assignable into `sx`.
 */
export interface HeaderCellColorSx {
    color: string;
}

export interface HeaderLabelSx {
    color: string;
    "&:hover": {color: string};
}

export interface HeaderArrowSx {
    color: string;
    opacity: number;
    "&:hover": {opacity: number};
}

/**
 * Sort-header color contract for `DataTable`'s column headers (plan
 * "Spaltenkopf-Filterung und Sortieranzeige", Standing constraint 5).
 *
 * Bug this module fixes, measured against the code: `TableSortLabel` carries its own root color
 * (`text.secondary`) and its own `&:hover` / `&.Mui-active` colors (`text.primary`), which overrides
 * the header cell's inherited `primary.contrastText`. `text.primary` is themed to `color_blue[2]`
 * (`AppThemeContext.tsx`), exactly the header's own background color, so the label and the sort arrow
 * turned unreadable on hover and on the active sort column.
 *
 * Contract, held stable across the Phase 1.2 rewrite: header text, sort arrow and dropdown indicator
 * carry `primary.contrastText` (white in light mode, `#333` in dark mode) in the neutral, the hover and
 * the active state alike, never a mode-independent color. This module stays icon-independent: it hands
 * back plain color/opacity fragments, never an MUI class name (`.Mui-active`,
 * `.MuiTableSortLabel-icon`). Phase 1.1 still renders through `TableSortLabel`, so `DataTable.tsx`
 * itself maps these fragments onto that component's class selectors; Phase 1.2 drops `TableSortLabel`
 * for a plain `ArrowUpward` / `ArrowDownward` icon driven by the `active` boolean directly, and reads
 * the very same functions unchanged.
 */

/**
 * Resolves `primary.contrastText` for the given palette mode without a `Theme` instance: white in
 * light mode, `#333` in dark mode (`AppThemeContext.tsx`).
 */
function resolveContrastColor(mode: PaletteMode): string {
    return mode === "dark" ? "#333" : "#fff";
}

/**
 * Header-cell text color fragment: the contrast color, unconditionally.
 */
export function resolveHeaderCellSx(mode: PaletteMode): HeaderCellColorSx {
    return {
        color: resolveContrastColor(mode)
    };
}

/**
 * Header-label (column text) color fragment, including the hover state: both stay the contrast color,
 * neither darkens on hover.
 */
export function resolveHeaderLabelSx(mode: PaletteMode): HeaderLabelSx {
    const color = resolveContrastColor(mode);
    return {
        color,
        "&:hover": {
            color
        }
    };
}

/**
 * Sort-arrow color and visibility fragment. The color is the contrast color in every state. The
 * neutral state hides the arrow (`opacity: 0`) until hovered, matching `TableSortLabel`'s own default
 * behavior; the active state keeps it permanently visible (`opacity: 1`), per Standing constraint 5.
 */
export function resolveHeaderArrowSx(mode: PaletteMode, active: boolean): HeaderArrowSx {
    const color = resolveContrastColor(mode);
    return {
        color,
        opacity: active ? 1 : 0,
        "&:hover": {
            opacity: 1
        }
    };
}

/**
 * Dropdown-indicator opacity: a constant 0.7 in every state and mode, so the indicator stays visible
 * without overloading the header (Standing constraint 5). No caller yet: the dropdown indicator itself
 * is built in Phase 1.2's `DataTableColumnHeader.tsx`, which wires this function to the `ArrowDropDown`
 * icon added there. Kept exported and tested ahead of that consumer, not dead code.
 */
export function resolveHeaderIndicatorOpacity(): number {
    return 0.7;
}
